/**
 * UserIdentityManager - User Identity Linking Management
 * Links users to external IDP identities
 */

import React, { useState, useEffect } from 'react';
import apiClient from '../../../assets/scripts/services/apiClient';
import {
  listIdpConfigurations,
  getUserIdentities,
  linkUserIdentity,
  unlinkUserIdentity,
} from './idpApi';

const UserIdentityManager = ({ orgId }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [identities, setIdentities] = useState([]);
  const [idps, setIdps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingIdentities, setLoadingIdentities] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Link modal state
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [selectedIdpId, setSelectedIdpId] = useState(null);
  const [subject, setSubject] = useState('');
  const [email, setEmail] = useState('');
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState('');

  useEffect(() => {
    loadIdps();
  }, [orgId]);

  useEffect(() => {
    if (selectedUser) {
      loadIdentities(selectedUser.id);
    }
  }, [selectedUser, orgId]);

  const loadIdps = async () => {
    try {
      const data = await listIdpConfigurations(orgId, true);
      setIdps(Array.isArray(data) ? data : []);
      if (data && data.length > 0) {
        setSelectedIdpId(data[0].id);
      }
    } catch (e) {
      console.error('Failed to load IDPs:', e);
    }
  };

  const searchUsers = async () => {
    if (!searchQuery.trim()) {
      setError('Please enter a search query');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSelectedUser(null);
      setIdentities([]);

      // Search users by email
      const response = await apiClient.get(`/organizations/${orgId}/users?search=${encodeURIComponent(searchQuery)}`);
      const userList = response?.users || response || [];
      setUsers(Array.isArray(userList) ? userList : []);

      if (userList.length === 0) {
        setError('No users found matching your search');
      }
    } catch (e) {
      console.error('Failed to search users:', e);
      setError('Failed to search users');
    } finally {
      setLoading(false);
    }
  };

  const loadIdentities = async (userId) => {
    try {
      setLoadingIdentities(true);
      const data = await getUserIdentities(orgId, userId);
      setIdentities(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Failed to load identities:', e);
      // Might be 404 if no identities linked
      setIdentities([]);
    } finally {
      setLoadingIdentities(false);
    }
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setEmail(user.email || '');
  };

  const handleOpenLinkModal = () => {
    setSubject('');
    setEmail(selectedUser?.email || '');
    setLinkError('');
    setShowLinkModal(true);
  };

  const handleLink = async (e) => {
    e.preventDefault();
    if (!selectedIdpId || !subject.trim()) {
      setLinkError('IDP and subject are required');
      return;
    }

    try {
      setLinking(true);
      setLinkError('');

      await linkUserIdentity(orgId, selectedUser.id, {
        idp_config_id: selectedIdpId,
        subject: subject.trim(),
        email: email.trim() || undefined,
      });

      setShowLinkModal(false);
      setSuccess('Identity linked successfully');
      setTimeout(() => setSuccess(''), 3000);
      loadIdentities(selectedUser.id);
    } catch (e) {
      console.error('Failed to link identity:', e);
      setLinkError(e.message || 'Failed to link identity');
    } finally {
      setLinking(false);
    }
  };

  const handleUnlink = async (identity) => {
    const idpName = getIdpName(identity.idp_config_id);
    if (!window.confirm(`Unlink this identity from ${idpName}? The user will not be able to login via this IDP.`)) {
      return;
    }

    try {
      await unlinkUserIdentity(orgId, selectedUser.id, identity.id);
      setSuccess('Identity unlinked successfully');
      setTimeout(() => setSuccess(''), 3000);
      loadIdentities(selectedUser.id);
    } catch (e) {
      console.error('Failed to unlink identity:', e);
      setError('Failed to unlink identity');
    }
  };

  const getIdpName = (idpConfigId) => {
    const idp = idps.find((i) => i.id === idpConfigId);
    return idp ? idp.name : 'Unknown IDP';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  return (
    <div className="user-identity-manager">
      <h5 className="mB-20">User Identity Management</h5>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')}></button>
        </div>
      )}

      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {success}
          <button type="button" className="btn-close" onClick={() => setSuccess('')}></button>
        </div>
      )}

      {/* User Search */}
      <div className="card mB-20">
        <div className="card-header">
          <h6 className="mB-0">Search User</h6>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-8">
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter email or name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && searchUsers()}
                />
                <button
                  className="btn btn-primary"
                  onClick={searchUsers}
                  disabled={loading}
                >
                  {loading ? (
                    <span className="spinner-border spinner-border-sm"></span>
                  ) : (
                    <>
                      <i className="ti-search mR-5"></i>
                      Search
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {users.length > 0 && (
            <div className="mT-15">
              <label className="form-label small text-muted">Select a user:</label>
              <div className="list-group">
                {users.map((user) => (
                  <button
                    key={user.id}
                    type="button"
                    className={`list-group-item list-group-item-action ${selectedUser?.id === user.id ? 'active' : ''}`}
                    onClick={() => handleSelectUser(user)}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <strong>{user.first_name} {user.last_name}</strong>
                        <p className="mB-0 small">{user.email}</p>
                      </div>
                      <span className="badge bg-secondary">ID: {user.id}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Selected User Identities */}
      {selectedUser && (
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <div>
              <h6 className="mB-0">
                {selectedUser.first_name} {selectedUser.last_name}
              </h6>
              <small className="text-muted">{selectedUser.email} | ID: {selectedUser.id}</small>
            </div>
            <button className="btn btn-primary btn-sm" onClick={handleOpenLinkModal}>
              <i className="ti-plus mR-5"></i>
              Link Identity
            </button>
          </div>
          <div className="card-body">
            <h6>Linked Identities</h6>

            {loadingIdentities ? (
              <div className="text-center p-4">
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : identities.length === 0 ? (
              <div className="text-center p-4 bgc-grey-100 bdrs-4">
                <i className="ti-id-badge fsz-xl c-grey-400 mB-10 d-block"></i>
                <p className="mB-10">No external identities linked.</p>
                <p className="text-muted small mB-0">
                  Click "Link Identity" to add an external identity from Auth0 or another IDP.
                </p>
              </div>
            ) : (
              <div className="list-group">
                {identities.map((identity) => (
                  <div key={identity.id} className="list-group-item">
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <strong>{getIdpName(identity.idp_config_id)}</strong>
                        <p className="mB-5 small">
                          <strong>Subject:</strong> <code>{identity.subject}</code>
                        </p>
                        {identity.email && (
                          <p className="mB-5 small">
                            <strong>Email:</strong> {identity.email}
                          </p>
                        )}
                        <p className="mB-0 small text-muted">
                          Linked: {formatDate(identity.create_date)}
                        </p>
                      </div>
                      <button
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => handleUnlink(identity)}
                      >
                        <i className="ti-unlink mR-5"></i>
                        Unlink
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Link Identity Modal */}
      {showLinkModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Link External Identity</h5>
                <button type="button" className="btn-close" onClick={() => setShowLinkModal(false)}></button>
              </div>
              <form onSubmit={handleLink}>
                <div className="modal-body">
                  {linkError && (
                    <div className="alert alert-danger">{linkError}</div>
                  )}

                  <p className="mB-15">
                    <strong>User:</strong> {selectedUser.first_name} {selectedUser.last_name} ({selectedUser.email})
                  </p>

                  <div className="mB-15">
                    <label className="form-label">Identity Provider *</label>
                    <select
                      className="form-select"
                      value={selectedIdpId || ''}
                      onChange={(e) => setSelectedIdpId(Number(e.target.value))}
                      required
                    >
                      {idps.map((idp) => (
                        <option key={idp.id} value={idp.id}>
                          {idp.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mB-15">
                    <label className="form-label">Subject (sub claim) *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder="auth0|507f1f77bcf86cd799439011"
                      required
                    />
                    <div className="form-text">
                      The 'sub' claim from the IDP token (user's unique ID in the IDP)
                    </div>
                  </div>

                  <div className="mB-15">
                    <label className="form-label">Email (optional)</label>
                    <input
                      type="email"
                      className="form-control"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="user@example.com"
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowLinkModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={linking}>
                    {linking ? (
                      <>
                        <span className="spinner-border spinner-border-sm mR-5"></span>
                        Linking...
                      </>
                    ) : (
                      'Link Identity'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserIdentityManager;
